import { cloneElement, isValidElement, type ReactNode } from 'react'
import { CirclePlus } from 'lucide-react'
import { AdminHeaderActionButton } from '@/components/patterns/admin-page'
import { Button, type ButtonProps } from '@/components/ui/button'
import { useOptionalIsReadOnly } from '@/lib/casl'

export type CreateActionButtonProps = ButtonProps & {
  /** `header` uses the page-header button sizing; `inline` defaults to a link-style button. */
  context?: 'header' | 'inline'
}

const CreateActionButton = ({
  children = 'Create New',
  onClick,
  variant,
  size,
  context = 'header',
  id,
  className,
  disabled = false,
  asChild = false,
  ...rest
}: CreateActionButtonProps) => {
  const { isReadOnly } = useOptionalIsReadOnly()

  if (isReadOnly) {
    return null
  }

  const isHeaderAction = context === 'header'
  const resolvedVariant = variant ?? (isHeaderAction ? 'default' : 'link')
  const icon = <CirclePlus className="size-4" />
  const ButtonComponent = isHeaderAction ? AdminHeaderActionButton : Button

  if (asChild && isValidElement<{ children?: ReactNode }>(children)) {
    return (
      <ButtonComponent
        variant={resolvedVariant}
        size={size}
        onClick={onClick}
        id={id}
        className={className}
        disabled={disabled}
        asChild
        {...rest}
      >
        {cloneElement(
          children,
          undefined,
          <>
            {icon}
            {children.props.children}
          </>
        )}
      </ButtonComponent>
    )
  }

  return (
    <ButtonComponent
      variant={resolvedVariant}
      size={size}
      onClick={onClick}
      id={id}
      className={className}
      disabled={disabled}
      {...rest}
    >
      {icon}
      {children}
    </ButtonComponent>
  )
}

export default CreateActionButton
